import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import '../styles/NotificationPreferences.css';

const DEFAULT_FORM = {
  in_app_enabled: true,
  email_enabled: false,
  whatsapp_enabled: false,
  push_enabled: false,
  quiet_hours_enabled: false,
  quiet_hours_start: '22:00',
  quiet_hours_end: '08:00',
  email_address: '',
  whatsapp_phone: '',
};

const base64UrlToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const NotificationPreferences = ({ embedded = false }) => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [pushState, setPushState] = useState({
    supported: false,
    permission: 'default',
    configured: false,
    publicKey: '',
    subscriptionCount: 0,
    currentEndpoint: '',
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isIosDevice, setIsIosDevice] = useState(false);
  const [isStandaloneMode, setIsStandaloneMode] = useState(false);

  const loadPreferences = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${API_BASE_URL}/notifications/preferences`);
      const data = response.data || {};
      setForm({
        in_app_enabled: Boolean(data.in_app_enabled ?? true),
        email_enabled: Boolean(data.email_enabled ?? false),
        whatsapp_enabled: Boolean(data.whatsapp_enabled ?? false),
        push_enabled: Boolean(data.push_enabled ?? false),
        quiet_hours_enabled: Boolean(data.quiet_hours_enabled ?? false),
        quiet_hours_start: data.quiet_hours_start ? String(data.quiet_hours_start).slice(0, 5) : '22:00',
        quiet_hours_end: data.quiet_hours_end ? String(data.quiet_hours_end).slice(0, 5) : '08:00',
        email_address: data.email_address || '',
        whatsapp_phone: data.whatsapp_phone || '',
      });
      setError('');
    } catch {
      setError('Tercihler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPushState = useCallback(async () => {
    const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
    const permission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';

    let configured = false;
    let publicKey = '';
    let subscriptionCount = 0;
    let currentEndpoint = '';

    try {
      const keyResponse = await apiClient.get(`${API_BASE_URL}/notifications/push-public-key`);
      configured = Boolean(keyResponse.data?.enabled);
      publicKey = String(keyResponse.data?.public_key || '');
    } catch {
      configured = false;
      publicKey = '';
    }

    try {
      const subResponse = await apiClient.get(`${API_BASE_URL}/notifications/push-subscriptions`);
      const subscriptions = Array.isArray(subResponse.data?.subscriptions) ? subResponse.data.subscriptions : [];
      subscriptionCount = subscriptions.length;
      currentEndpoint = subscriptions[0]?.endpoint || '';
    } catch {
      subscriptionCount = 0;
      currentEndpoint = '';
    }

    setPushState({
      supported,
      permission,
      configured,
      publicKey,
      subscriptionCount,
      currentEndpoint,
    });
  }, []);

  useEffect(() => {
    const userAgent = window.navigator.userAgent || '';
    const ios = /iphone|ipad|ipod/i.test(userAgent);
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    setIsIosDevice(ios);
    setIsStandaloneMode(standalone);
  }, []);

  useEffect(() => {
    loadPreferences();
    loadPushState();
  }, [loadPreferences, loadPushState]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const persistPreferences = async (nextForm) => {
    await apiClient.put(`${API_BASE_URL}/notifications/preferences`, nextForm);
  };

  const ensureServiceWorkerRegistration = async () => {
    const swUrl = `${process.env.PUBLIC_URL || ''}/sw.js`;
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      registration = await navigator.serviceWorker.register(swUrl);
    }
    return registration;
  };

  const handleEnablePush = async () => {
    setPushBusy(true);
    setError('');
    setMessage('');

    try {
      if (!pushState.supported) {
        throw new Error('Bu tarayıcıda push bildirimi desteklenmiyor.');
      }
      if (!pushState.publicKey) {
        throw new Error('Sunucuda VAPID ayarı eksik. Push bildirimi açılamadı.');
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Tarayıcı bildirim izni verilmedi.');
      }

      const registration = await ensureServiceWorkerRegistration();
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: base64UrlToUint8Array(pushState.publicKey),
        });
      }

      await apiClient.post(`${API_BASE_URL}/notifications/push-subscriptions`, {
        subscription: subscription.toJSON(),
      });

      const nextForm = { ...form, push_enabled: true };
      setForm(nextForm);
      await persistPreferences(nextForm);
      setMessage('Web push bildirimi etkinleştirildi.');
      await loadPushState();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || 'Push bildirimi etkinleştirilemedi.');
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setPushBusy(true);
    setError('');
    setMessage('');

    try {
      let endpoint = pushState.currentEndpoint;

      if (pushState.supported) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            endpoint = subscription.endpoint || endpoint;
            await subscription.unsubscribe();
          }
        }
      }

      if (endpoint) {
        await apiClient.delete(`${API_BASE_URL}/notifications/push-subscriptions`, {
          data: { endpoint },
        });
      }

      const nextForm = { ...form, push_enabled: false };
      setForm(nextForm);
      await persistPreferences(nextForm);
      setMessage('Web push bildirimi kapatıldı.');
      await loadPushState();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Push bildirimi kapatılamadı.');
    } finally {
      setPushBusy(false);
    }
  };

  const handleSendTestNotification = async () => {
    setTesting(true);
    setError('');
    setMessage('');
    try {
      const response = await apiClient.post(`${API_BASE_URL}/notifications/test-self`, {});
      const delivered = Array.isArray(response.data?.delivered_channels) ? response.data.delivered_channels : [];
      if (delivered.length > 0) {
        setMessage(`Test bildirimi gönderildi. Kanallar: ${delivered.join(', ')}`);
      } else {
        setMessage('Test isteği işlendi fakat aktif kanal bulunamadı. Tercihleri kontrol edin.');
      }
      await loadPushState();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Test bildirimi gönderilemedi.');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await persistPreferences(form);
      setMessage('Bildirim tercihleri kaydedildi.');
      await loadPushState();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Tercihler kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className={embedded ? 'notification-preferences-embedded' : 'notification-preferences-page'}>Yükleniyor...</div>;
  }

  return (
    <div className={embedded ? 'notification-preferences-embedded' : 'notification-preferences-page'}>
      <div className="notification-preferences-card">
        {!embedded && (
          <>
            <h1>Bildirim Tercihleri</h1>
            <p>Kanalları açıp kapatabilir, sessiz saat aralığını belirleyebilirsiniz.</p>
          </>
        )}

        {error && <div className="pref-alert error">{error}</div>}
        {message && <div className="pref-alert success">{message}</div>}

        <form onSubmit={handleSave}>
          <label className="pref-row">
            <input
              type="checkbox"
              checked={form.in_app_enabled}
              onChange={(event) => updateField('in_app_enabled', event.target.checked)}
            />
            <span>Uygulama içi bildirimler açık</span>
          </label>

          <label className="pref-row">
            <input
              type="checkbox"
              checked={form.email_enabled}
              onChange={(event) => updateField('email_enabled', event.target.checked)}
            />
            <span>E-posta bildirimleri açık</span>
          </label>

          <div className="pref-field">
            <label>E-posta adresi</label>
            <input
              type="email"
              value={form.email_address}
              onChange={(event) => updateField('email_address', event.target.value)}
              placeholder="ornek@firma.com"
            />
          </div>

          <label className="pref-row">
            <input
              type="checkbox"
              checked={form.whatsapp_enabled}
              onChange={(event) => updateField('whatsapp_enabled', event.target.checked)}
            />
            <span>WhatsApp bildirimleri açık</span>
          </label>

          <div className="pref-field">
            <label>WhatsApp numarası (E.164)</label>
            <input
              type="text"
              value={form.whatsapp_phone}
              onChange={(event) => updateField('whatsapp_phone', event.target.value)}
              placeholder="+905xxxxxxxxx"
            />
          </div>

          <label className="pref-row">
            <input
              type="checkbox"
              checked={form.push_enabled}
              onChange={(event) => updateField('push_enabled', event.target.checked)}
            />
            <span>Web push bildirimi açık</span>
          </label>

          <div className="push-box">
            <div className="push-status-line">
              <strong>Tarayıcı desteği:</strong> {pushState.supported ? 'Var' : 'Yok'}
            </div>
            <div className="push-status-line">
              <strong>İzin durumu:</strong> {pushState.permission}
            </div>
            <div className="push-status-line">
              <strong>Sunucu VAPID:</strong> {pushState.publicKey ? 'Hazır' : 'Eksik'}
            </div>
            <div className="push-status-line">
              <strong>Aktif cihaz:</strong> {pushState.subscriptionCount}
            </div>
            {isIosDevice && !isStandaloneMode && (
              <div className="push-hint">iPhone push için uygulamayı ana ekrana ekleyip oradan açın.</div>
            )}
            <div className="push-actions-row">
              <button
                type="button"
                className="push-action-btn"
                onClick={handleEnablePush}
                disabled={pushBusy || !pushState.supported || !pushState.publicKey}
              >
                {pushBusy ? 'İşleniyor...' : 'Tarayıcıyı Bağla'}
              </button>
              <button
                type="button"
                className="push-action-btn secondary"
                onClick={handleDisablePush}
                disabled={pushBusy || (!pushState.currentEndpoint && pushState.subscriptionCount === 0)}
              >
                Bağlantıyı Kaldır
              </button>
              <button
                type="button"
                className="push-action-btn secondary"
                onClick={loadPushState}
                disabled={pushBusy}
              >
                Durumu Yenile
              </button>
              <button
                type="button"
                className="push-action-btn secondary"
                onClick={handleSendTestNotification}
                disabled={testing}
              >
                {testing ? 'Gönderiliyor...' : 'Test Bildirimi Gönder'}
              </button>
            </div>
          </div>

          <label className="pref-row">
            <input
              type="checkbox"
              checked={form.quiet_hours_enabled}
              onChange={(event) => updateField('quiet_hours_enabled', event.target.checked)}
            />
            <span>Sessiz saatler aktif</span>
          </label>

          <div className="pref-time-grid">
            <div className="pref-field">
              <label>Sessiz saat başlangıcı</label>
              <input
                type="time"
                value={form.quiet_hours_start}
                onChange={(event) => updateField('quiet_hours_start', event.target.value)}
              />
            </div>
            <div className="pref-field">
              <label>Sessiz saat bitişi</label>
              <input
                type="time"
                value={form.quiet_hours_end}
                onChange={(event) => updateField('quiet_hours_end', event.target.value)}
              />
            </div>
          </div>

          <button type="submit" disabled={saving}>
            {saving ? 'Kaydediliyor...' : 'Tercihleri Kaydet'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default NotificationPreferences;
